/**
 * Conditional Logger Utility
 *
 * Provides environment-aware logging to prevent sensitive data exposure in production.
 * - Debug/info logs only appear in development
 * - Errors always logged for monitoring
 *
 * Security: Prevents console.log statements from exposing tokens, user data, etc.
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Redact UUID identifiers from strings to prevent case/user ID exposure in
 * production logs. Preserves surrounding text for debugging
 * (e.g. `/api/cases/[REDACTED]/overview`).
 */
export function redactUuids(text: string): string {
  const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
  return text.replaceAll(UUID_RE, '[REDACTED]');
}

/**
 * Best-effort UUID redaction of a single log argument for production output.
 * Strings are redacted directly; plain objects are redacted via a JSON
 * round-trip; Errors are flattened to a redacted `name: message` (stack is
 * dropped — warn is not an error channel). Anything else passes through.
 */
function redactArg(arg: unknown): unknown {
  if (typeof arg === 'string') return redactUuids(arg);
  if (arg instanceof Error) return redactUuids(`${arg.name}: ${arg.message}`);
  if (arg && typeof arg === 'object') {
    try {
      return JSON.parse(redactUuids(JSON.stringify(arg)));
    } catch {
      return arg;
    }
  }
  return arg;
}

export const logger = {
  /**
   * Debug-level logging (development only)
   * Use for detailed debugging information
   */
  debug: (...args: unknown[]) => {
    if (isDevelopment) {
      console.log('[DEBUG]', ...args);
    }
  },

  /**
   * Info-level logging (development only)
   * Use for general informational messages
   */
  info: (...args: unknown[]) => {
    if (isDevelopment) {
      console.log('[INFO]', ...args);
    }
  },

  /**
   * Warning-level logging (always logged).
   *
   * Emits in production too (Cloud Run ships stdout/stderr to Cloud Logging)
   * so auth-recovery signals — `[AuthReset]` reasons, `[SessionAPI]` absolute-
   * cap rejections, `lockSession` failures — are diagnosable server-side
   * without a live repro (gh #946). Args are UUID-redacted in production to
   * avoid leaking case/user IDs into logs; dev output is left verbatim.
   */
  warn: (...args: unknown[]) => {
    if (isDevelopment) {
      console.warn('[WARN]', ...args);
      return;
    }
    console.warn('[WARN]', ...args.map(redactArg));
  },

  /**
   * Error-level logging (always logged)
   * Use for errors that need monitoring in production
   */
  error: (...args: unknown[]) => {
    console.error('[ERROR]', ...args);
  },
};
